import React, { useMemo, useState } from 'react';
import { accountingApi } from '../../services/accountingApi';
import AccountingDatePicker from './AccountingDatePicker';
import AccountingMoneyInput, { formatAccountingMoney } from './AccountingMoneyInput';
import { handlePhoneInput } from '../../utils/inputUtils';

const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Lima', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const emptyPayment = { payment_method_id: '', ruc_bank_id: '', amount: '', reference: '', note: '' };
const empty = {
    movement_date: today(),
    operation_type: 'EGRESO',
    sede_id: '',
    city: '',
    phone_number: '',
    amount: '',
    detail: '',
    payment_details: [{ ...emptyPayment }],
};

const money = (n) => Number(n || 0);
// Misma normalización y regla de celular peruano utilizada en Cards.jsx.
const normalizePeruMobileUI = (value, { allowEmpty = false } = {}) => {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) return allowEmpty ? '' : '9';
    let normalized = digits;
    if (normalized[0] !== '9') normalized = '9' + normalized;
    return normalized.slice(0, 9);
};
const isValidPeruMobile = (value) => /^9\d{8}$/.test(String(value || ''));

// Prefer the city's explicit metadata. Older sede records encode the city as the first word.
const cityFromSede = (sede) => {
    if (!sede) return '';
    const explicit = sede.city || sede.ciudad || sede.sd_city || sede.city_name || sede.ciudad_nombre;
    if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
    return String(sede.sd_sede || sede.sd_location || '').trim().split(/\s+/)[0] || '';
};

export default function MovementForm({ meta, onSaved, initialOperation = 'EGRESO', onCancel, onDirtyChange, onSavingChange }) {
    const [mode, setMode] = useState(initialOperation === 'TRANSFER' ? 'TRANSFER' : 'NORMAL');
    const [form, setForm] = useState(() => ({ ...empty, operation_type: initialOperation === 'INGRESO' ? 'INGRESO' : 'EGRESO', payment_details: [{ ...emptyPayment }] }));
    const [saving, setSaving] = useState(false);
    const [phoneError, setPhoneError] = useState('');

    const markDirty = () => { if (onDirtyChange) onDirtyChange(true); };
    const setOperation = (operation) => {
        if (saving) return;
        markDirty();
        setMode(operation === 'TRANSFER' ? 'TRANSFER' : 'NORMAL');
        setForm(p => ({ ...p, operation_type: operation === 'INGRESO' ? 'INGRESO' : 'EGRESO' }));
    };
    const rucBanks = meta.rucBanks || [];
    const banksForRuc = useMemo(() => {
        if (!form.ruc_id) return rucBanks;
        return rucBanks.filter((x) => String(x.ruc_id) === String(form.ruc_id));
    }, [form.ruc_id, rucBanks]);

    const totals = useMemo(() => {
        const total = (form.payment_details || []).reduce((a, x) => a + money(x.amount), 0);
        const cash = (form.payment_details || []).reduce((a, x) => {
            const isCash = !x.payment_method_id && !x.ruc_bank_id;
            return a + (isCash ? money(x.amount) : 0);
        }, 0);
        const online = Math.max(total - cash, 0);
        return { total, cash, online };
    }, [form.payment_details, form.cash_amount]);

    const change = (name, value) => {
        markDirty();
        const patch = { [name]: value };
        if (name === 'sede_id') {
            const sede = (meta.sedes || []).find((x) => String(x.sd_id || x.id) === String(value));
            patch.city = cityFromSede(sede);
        }
        if (name === 'ruc_id') patch.bank_id = '';
        setForm((p) => ({ ...p, ...patch }));
    };

    const changePayment = (idx, name, value) => {
        markDirty();
        setForm((p) => {
            const list = [...(p.payment_details || [])];
            list[idx] = { ...list[idx], [name]: value };
            return { ...p, payment_details: list };
        });
    };

    const addPayment = () => { markDirty(); setForm((p) => ({ ...p, payment_details: [...(p.payment_details || []), { ...emptyPayment }] })); };
    const removePayment = (idx) => { markDirty(); setForm((p) => ({ ...p, payment_details: (p.payment_details || []).filter((_, i) => i !== idx) })); };

    const confirmSave = () => window.confirm('Revise los datos antes de guardar. Si todo está correcto, confirme para registrar.');

    const payload = () => ({
        ...form,
        // Transfers have no physical store/customer contact; clear stale form values.
        ...(mode === 'TRANSFER' ? { sede_id: null, city: null, phone_number: null } : {}),
        movement_type: mode,
        amount: mode === 'TRANSFER' ? form.amount : totals.total,
        cash_amount: totals.cash,
        online_amount: totals.online,
        payment_details: mode === 'NORMAL' ? (form.payment_details || []).filter((x) => money(x.amount) > 0) : [],
    });

    const submitNormal = async () => {
        if (!isValidPeruMobile(form.phone_number)) {
            setPhoneError('El número de celular debe comenzar con 9 y contener exactamente 9 dígitos.');
            return;
        }
        setPhoneError('');
        if (!confirmSave()) return;
        setSaving(true);
        if (onSavingChange) onSavingChange(true);
        try {
            await accountingApi.createMovement(payload());
            await onSaved();
            setForm({ ...empty, payment_details: [{ ...emptyPayment }] });
            if (onDirtyChange) onDirtyChange(false);
            alert('Registro guardado correctamente.');
        } catch (e) {
            alert(e.response?.data?.message || 'No se pudo guardar el registro.');
        } finally {
            setSaving(false);
            if (onSavingChange) onSavingChange(false);
        }
    };

    const submitTransfer = async () => {
        if (!confirmSave()) return;
        setSaving(true);
        if (onSavingChange) onSavingChange(true);
        try {
            await accountingApi.createTransfer(payload());
            await onSaved();
            setForm({ ...empty, payment_details: [{ ...emptyPayment }] });
            if (onDirtyChange) onDirtyChange(false);
            alert('Transferencia registrada correctamente con egreso e ingreso vinculados.');
        } catch (e) {
            alert(e.response?.data?.message || 'No se pudo registrar la transferencia.');
        } finally {
            setSaving(false);
            if (onSavingChange) onSavingChange(false);
        }
    };

    const select = (name, label, rows, getLabel) => (
        <label>
            <span>{label}</span>
            <select value={form[name] || ''} onChange={(e) => change(name, e.target.value)}>
                <option value="">Seleccionar</option>
                {rows.map((x) => <option key={x.id || x.sd_id} value={x.id || x.sd_id}>{getLabel(x)}</option>)}
            </select>
        </label>
    );

    return (
        <section className="accCard movementForm accForm">
            <div className="accOperationChoices" role="group" aria-label="Tipo de movimiento">
                <button type="button" className={mode === 'NORMAL' && form.operation_type === 'INGRESO' ? 'selected income' : ''} aria-pressed={mode === 'NORMAL' && form.operation_type === 'INGRESO'} onClick={() => setOperation('INGRESO')}><span className="accChoiceIcon" aria-hidden="true">↙</span><span><strong>Registrar ingreso</strong><small>Dinero que entra a la empresa</small></span></button>
                <button type="button" className={mode === 'NORMAL' && form.operation_type === 'EGRESO' ? 'selected expense' : ''} aria-pressed={mode === 'NORMAL' && form.operation_type === 'EGRESO'} onClick={() => setOperation('EGRESO')}><span className="accChoiceIcon" aria-hidden="true">↗</span><span><strong>Registrar egreso</strong><small>Gastos y pagos realizados</small></span></button>
                <button type="button" className={mode === 'TRANSFER' ? 'selected transfer' : ''} aria-pressed={mode === 'TRANSFER'} onClick={() => setOperation('TRANSFER')}><span className="accChoiceIcon" aria-hidden="true">⇄</span><span><strong>Transferencia entre bancos</strong><small>Movimiento entre cuentas</small></span></button>
            </div>

            <div className="accFormSection">
                <div className="accFormSectionHead"><span className="accSectionNumber">01</span><div><h3>Información general</h3><p>{mode === 'TRANSFER' ? 'Indica la fecha de la transferencia bancaria.' : 'Selecciona la sede para completar automáticamente la ciudad.'}</p></div></div>
                <div className={`movementGrid ${mode === 'TRANSFER' ? 'accTransferGeneral' : ''}`}>
                    <label><span>{mode === 'TRANSFER' ? 'Fecha de transferencia' : 'Fecha'}</span><AccountingDatePicker min={meta.minDate || '2026-07-03'} value={form.movement_date || ''} onChange={(value) => change('movement_date', value)} /></label>
                    {mode === 'NORMAL' && <>
                        {select('sede_id', 'Sede', meta.sedes || [], (x) => `${x.sd_sede} - ${x.sd_location || ''}`)}
                        <label className="accAutoCity"><span>Ciudad <small>Automático · no editable</small></span><input value={form.city || ''} disabled placeholder="Selecciona una sede" aria-label="Ciudad asignada automáticamente" /></label>
                        <label><span>Número de celular</span><input type="tel" inputMode="numeric" autoComplete="tel" maxLength={9} value={form.phone_number || ''} onChange={(e) => { handlePhoneInput(e); change('phone_number', normalizePeruMobileUI(e.target.value, { allowEmpty: true })); if (phoneError) setPhoneError(''); }} onBlur={() => { if (form.phone_number && !isValidPeruMobile(form.phone_number)) setPhoneError('El número de celular debe comenzar con 9 y contener exactamente 9 dígitos.'); }} placeholder="9 dígitos" aria-invalid={Boolean(phoneError)} aria-describedby={phoneError ? 'acc-phone-error' : undefined} />{phoneError && <small id="acc-phone-error" role="alert" style={{ color: '#b91c1c', fontWeight: 600 }}>{phoneError}</small>}</label>
                    </>}
                </div>
            </div>
            <div className="accFormSection">
                <div className="accFormSectionHead"><span className="accSectionNumber">02</span><div><h3>Clasificación del movimiento</h3><p>Indica la operación, empresa, responsables y cuentas relacionadas.</p></div></div>
                <div className="movementGrid">
                    {mode === 'NORMAL' && <>
                        <label><span>Tipo</span><select value={form.operation_type || 'EGRESO'} onChange={(e) => change('operation_type', e.target.value)}><option value="EGRESO">Egreso</option><option value="INGRESO">Ingreso</option></select></label>
                        {select('ruc_id', 'RUC empresa', meta.rucs || [], (x) => x.alias || x.business_name)}
                        {select('bank_id', 'Banco / cuenta principal', banksForRuc, (x) => x.label)}
                        {select('supplier_id', 'Proveedor', meta.suppliers || [], (x) => x.business_name)}
                    </>}
                    {mode === 'TRANSFER' && <>
                        {select('origin_ruc_bank_id', 'Banco origen', rucBanks, (x) => x.label)}
                        {select('destination_ruc_bank_id', 'Banco destino', rucBanks, (x) => x.label)}
                        <label><span>Monto transferencia</span><AccountingMoneyInput value={form.amount ?? ''} onChange={(value) => change('amount', value)} /></label>
                    </>}
                    {select('area_id', 'Área', meta.areas || [], (x) => x.name)}
                    {select('category_id', 'Categoría', meta.categories || [], (x) => `${x.operation_type} - ${x.name}`)}
                    {select('requester_user_id', 'Solicitante interno', meta.users || [], (x) => `${x.name || ''} ${x.paternalSur || ''}`)}
                    {select('requester_external_id', 'Solicitante externo', meta.externalRequesters || [], (x) => x.full_name)}
                </div>
            </div>
            <div className="accFormSection">
                <div className="accFormSectionHead"><span className="accSectionNumber">03</span><div><h3>Comprobante y descripción</h3><p>Documenta el movimiento para facilitar su consulta y auditoría.</p></div></div>
                <div className="movementGrid">
                    {select('receipt_type_id', 'Comprobante', meta.receiptTypes || [], (x) => x.name)}
                    <label><span>Número comprobante</span><input value={form.receipt_number || ''} onChange={(e) => change('receipt_number', e.target.value)} /></label>
                    <label className="wide"><span>Detalle</span><textarea value={form.detail || ''} onChange={(e) => change('detail', e.target.value)} placeholder="Describe el motivo de la operación" /></label>
                </div>
            </div>

            {mode === 'NORMAL' && (
                <div className="paymentBox">
                    <div className="paymentHead">
                        <div className="accFormSectionHead"><span className="accSectionNumber">04</span><div><h3>Distribución del pago</h3><p>Registra el importe de cada medio de pago. Puedes combinar efectivo y métodos digitales.</p></div></div>
                        <button type="button" onClick={addPayment}>+ Agregar método</button>
                    </div>
                    <div className="accPaymentList">
                        {(form.payment_details || []).map((p, idx) => (
                            <div className="paymentRow" key={idx}>
                                <div className="accPaymentRowHead"><strong>Método de pago {idx + 1}</strong><button type="button" className="paymentRemove" onClick={() => removePayment(idx)} disabled={(form.payment_details || []).length === 1}>Quitar método</button></div>
                                <label><span>Método</span><select value={p.payment_method_id || ''} onChange={(e) => changePayment(idx, 'payment_method_id', e.target.value)}><option value="">Efectivo</option>{(meta.paymentMethods || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
                                <label><span>Banco / cuenta RUC</span><select value={p.ruc_bank_id || ''} onChange={(e) => changePayment(idx, 'ruc_bank_id', e.target.value)}><option value="">No aplica</option>{rucBanks.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select></label>
                                <label><span>Monto (S/)</span><AccountingMoneyInput value={p.amount ?? ''} onChange={(value) => changePayment(idx, 'amount', value)} /></label>
                                <label><span>Referencia</span><input value={p.reference || ''} onChange={(e) => changePayment(idx, 'reference', e.target.value)} placeholder="N.º de operación (si aplica)" /></label>
                            </div>
                        ))}
                    </div>
                    <div className="paymentTotals" aria-live="polite">
                        <span>Efectivo <b>{formatAccountingMoney(totals.cash)}</b></span>
                        <span>Otros métodos <b>{formatAccountingMoney(totals.online)}</b></span>
                        <span className="accPaymentGrandTotal">Total del movimiento <b>{formatAccountingMoney(totals.total)}</b></span>
                    </div>
                </div>
            )}

            <div className="accModalFormActions">
            {onCancel && <button className="accCancel" type="button" disabled={saving} onClick={onCancel}>Cancelar</button>}
            <button className="accPrimary" type="button" disabled={saving} onClick={mode === 'TRANSFER' ? submitTransfer : submitNormal}>
                {saving ? 'Guardando…' : mode === 'TRANSFER' ? 'Registrar transferencia' : 'Guardar registro'}
            </button>
            </div>
        </section>
    );
}
